import React, { useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Button, TextField, Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";

import { groupVariables } from "../../utils/messageTemplateApi";

const useStyles = makeStyles({
  root: { display: "flex", flexDirection: "column", gap: "8px" },
  header: {
    display: "flex",
    alignItems: "center",
    gap: "12px",
    flexWrap: "wrap",
  },
  heading: { fontSize: "14px", fontWeight: 700, color: "#101828" },
  groupHeading: {
    fontSize: "12px",
    fontWeight: 700,
    color: "#344054",
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    margin: "4px 0",
  },
  hint: { fontSize: "12px", color: "#475467" },
  search: { minWidth: "220px", flex: "1 1 220px" },
  list: {
    display: "flex",
    flexWrap: "wrap",
    gap: "6px",
    listStyle: "none",
    margin: 0,
    padding: 0,
  },
  chip: {
    minHeight: "44px",
    textTransform: "none",
    borderRadius: "22px",
    borderColor: "#0b5cad",
    color: "#0a3d73",
    backgroundColor: "#eef4ff",
    padding: "4px 12px",
    textAlign: "left",
    lineHeight: 1.2,
    "&:hover": { backgroundColor: "#dbe7ff" },
  },
  token: {
    display: "block",
    fontFamily: "monospace",
    fontSize: "11px",
    color: "#344054",
  },
  required: { color: "#b42318", marginLeft: "4px" },
  touch: { minHeight: "44px", textTransform: "none" },
  requested: {
    fontSize: "12px",
    color: "#7a2e0e",
    backgroundColor: "#fffaeb",
    border: "1px solid #fedf89",
    borderRadius: "6px",
    padding: "6px 10px",
  },
});

const KIND_HINTS = {
  URL: "link, inserted unescaped",
  HTML: "HTML fragment, inserted unescaped",
  SECTION: "shows its content only when set",
  TEXT: "text, HTML-escaped",
};

/**
 * The variables the sending code supplies for this template, grouped (Message, Diver, Coach, …)
 * and searchable. Activating one inserts its tag at the caret of whichever field (subject or
 * body) was focused last. "Request a variable" covers values the list does not have yet.
 */
const VariablePalette = ({
  variables,
  onInsert,
  onRequest,
  requested,
  targetLabel,
  declared,
}) => {
  const classes = useStyles();
  const [search, setSearch] = useState("");
  const groups = useMemo(
    () => groupVariables(variables, search),
    [variables, search],
  );

  if (!declared) {
    return (
      <Typography className={classes.hint}>
        This build has no variable list for this template, so any {"{{name}}"}{" "}
        is accepted without checking. Deploy the code that sends it to get the
        full editor support.
      </Typography>
    );
  }

  const requestButton = onRequest && (
    <Button variant="outlined" className={classes.touch} onClick={onRequest}>
      Request a variable…
    </Button>
  );

  if (!variables.length) {
    return (
      <Box className={classes.root}>
        <Typography className={classes.hint}>
          This template receives no variables.
        </Typography>
        {requestButton}
      </Box>
    );
  }

  return (
    <Box
      className={classes.root}
      component="section"
      aria-labelledby="mt-variables-heading"
    >
      <Box className={classes.header}>
        <Typography
          id="mt-variables-heading"
          component="h2"
          className={classes.heading}
        >
          Variables
        </Typography>
        <TextField
          size="small"
          variant="outlined"
          className={classes.search}
          id="mt-variable-search"
          label="Search variables"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
        {requestButton}
      </Box>
      <Typography className={classes.hint}>
        Inserts into: {targetLabel}
      </Typography>
      {requested.length > 0 && (
        <Typography className={classes.requested} role="status">
          Requested with this save:{" "}
          {requested.map((r) => `{{${r.tag}}}`).join(", ")}. They are sent empty
          until a developer connects them.
        </Typography>
      )}
      {groups.length === 0 && (
        <Typography className={classes.hint}>
          No variable matches “{search}”.
          {onRequest ? " Request it if the code does not supply it yet." : ""}
        </Typography>
      )}
      {groups.map(({ group, variables: items }) => {
        const headingId = `mt-variables-${group.replace(/\W+/g, "-")}`;
        return (
          <Box key={group} component="section" aria-labelledby={headingId}>
            <Typography
              id={headingId}
              component="h3"
              className={classes.groupHeading}
            >
              {group}
            </Typography>
            <ul className={classes.list}>
              {items.map((v) => (
                <li key={v.name}>
                  <Button
                    variant="outlined"
                    className={classes.chip}
                    onClick={() => onInsert(v)}
                    aria-label={`Insert ${group} ${v.label}${
                      v.required ? " (required)" : ""
                    } into ${targetLabel}`}
                    title={[v.token, v.description, KIND_HINTS[v.kind]]
                      .filter(Boolean)
                      .join(" — ")}
                  >
                    <span>
                      {v.label}
                      {v.required && (
                        <span className={classes.required} aria-hidden="true">
                          *
                        </span>
                      )}
                      <span className={classes.token}>{v.token}</span>
                    </span>
                  </Button>
                </li>
              ))}
            </ul>
          </Box>
        );
      })}
    </Box>
  );
};

VariablePalette.propTypes = {
  variables: PropTypes.arrayOf(
    PropTypes.shape({
      name: PropTypes.string,
      group: PropTypes.string,
      label: PropTypes.string,
      description: PropTypes.string,
      kind: PropTypes.string,
      required: PropTypes.bool,
      token: PropTypes.string,
    }),
  ),
  onInsert: PropTypes.func.isRequired,
  onRequest: PropTypes.func,
  requested: PropTypes.arrayOf(
    PropTypes.shape({ tag: PropTypes.string, description: PropTypes.string }),
  ),
  targetLabel: PropTypes.string,
  declared: PropTypes.bool,
};

VariablePalette.defaultProps = {
  variables: [],
  onRequest: null,
  requested: [],
  targetLabel: "body",
  declared: true,
};

export default VariablePalette;
